import type { FC, ReactNode } from 'react'

import { SplitChars } from '@/components/motion/split-text'
import Parallax from '@/components/motion/parallax'
import RollingNumber from '@/components/motion/rolling-number'
import Img from '@/components/generals/img'
import { editorial } from '@/constants/media'

const SPECS = [
    { value: '16', unit: 'oz Japanese selvedge', detail: "Unsanforized, unwashed, woven on vintage shuttle looms in Okayama." },
    { value: '03', unit: 'Layer recycled shell', detail: "Bonded, taped and tested through 200 hours of rain simulation." },
    { value: '08', unit: 'Gauge merino knit', detail: "Fully-fashioned knits linked stitch by stitch in Portugal." },
    { value: '300', unit: 'Pieces per run', detail: "Every garment is numbered. When a run sells out, it’s gone." }
]

const Materials: FC = (): ReactNode => {
    return (
        <section id='materials' aria-label='Materials' className='relative bg-bone text-ink'>
            <div className='pb-16 pt-24 gutter md:pb-20 md:pt-32'>
                <div className='flex items-center justify-between gap-4 label-mono text-ink/60'>
                    <span data-reveal='fade'>
                        (05) — Materials
                    </span>

                    <span data-reveal='fade'>
                        Spec sheet, SS26
                    </span>
                </div>

                <div className='mt-10 grid gap-10 md:grid-cols-12 md:items-end md:gap-8'>
                    <h2 data-reveal='chars' className='display-xl uppercase md:col-span-8'>
                        <span className='block'>
                            <SplitChars text='Built heavier.' />
                        </span>

                        <span className='block'>
                            <SplitChars text='Worn longer.' />
                        </span>
                    </h2>

                    <p data-reveal='fade' className='max-w-[36ch] lead text-ink/75 md:col-span-4'>
                        We source like engineers and finish like tailors. The numbers below aren’t marketing — they’re the spec sheet we hand to our makers.
                    </p>
                </div>
            </div>

            <div data-reveal='image' className='relative mx-[var(--gutter)] overflow-hidden'>
                <Parallax amount={14} className='h-[44vh] md:h-[64vh]'>
                    <Img src={editorial.materialsShell} alt="A black hooded technical shell beaded with rain, hanging against a sunlit concrete wall" sizes='100vw' className='object-[40%_30%]' />
                </Parallax>

                <p className='absolute bottom-4 left-4 bg-bone px-2 py-1 meta-mono text-ink'>
                    Fig. 04 — 3L shell, taped and sealed
                </p>
            </div>

            <div className='grid grid-cols-1 gap-x-8 gap-y-14 pb-28 pt-16 gutter sm:grid-cols-2 md:pb-36 lg:grid-cols-4'>
                {SPECS.map((spec, index) => (
                    <div key={spec.unit} className='relative pt-6'>
                        <span data-reveal='line' aria-hidden='true' className='absolute inset-x-0 top-0 h-px bg-ink/20' />

                        <RollingNumber
                            value={spec.value}
                            delay={index * 0.12}
                            className='font-heading text-[clamp(64px,7vw,116px)] text-ink'
                        />

                        <p data-reveal='fade' className='mt-4 label-mono text-ink'>
                            {spec.unit}
                        </p>

                        <p data-reveal='fade' className='mt-3 max-w-[30ch] body-copy text-ink/65'>
                            {spec.detail}
                        </p>
                    </div>
                ))}
            </div>
        </section>
    )
}

export default Materials
